import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Users } from 'lucide-react';
import type { TrackerUser } from '@pms/shared';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCount } from './trackerConstants';

interface Props {
  users: TrackerUser[];
  /** Null = Everyone. */
  value: string | null;
  onChange: (userId: string | null) => void;
}

const displayName = (u: TrackerUser): string => u.name || u.userId;

function initials(u: TrackerUser): string {
  const parts = displayName(u).trim().split(/\s+/);
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase() || '?';
}

function Avatar({ user }: { user: TrackerUser }) {
  return (
    <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground">
      {initials(user)}
      {user.online && (
        <span
          className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-emerald-500"
          aria-label="Online"
        />
      )}
    </span>
  );
}

/**
 * Searchable single-select person filter for the Screenshots tab. Online people sort
 * first so the ones worth watching are at the top. Same inline-panel approach as the
 * other searchable pickers in the app (no Radix Popover), kept local to tracking
 * because importing across modules is banned.
 */
export function TrackerPersonPicker({ users, value, onChange }: Props) {
  const [open, setOpenState] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Every close path goes through here, so the next open starts unfiltered.
  function setOpen(next: boolean) {
    setOpenState(next);
    if (!next) setSearch('');
  }

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sorted = useMemo(
    () => [...users].sort((a, b) =>
      Number(b.online) - Number(a.online) || displayName(a).localeCompare(displayName(b))),
    [users],
  );

  const q = search.trim().toLowerCase();
  // Same fields the People tab search covers.
  const matches = q
    ? sorted.filter((u) => `${u.name} ${u.email} ${u.userId} ${u.machine ?? ''}`.toLowerCase().includes(q))
    : sorted;
  const showEveryone = !q || 'everyone'.includes(q);

  const selected = value ? users.find((u) => u.userId === value) : undefined;
  const onlineCount = users.filter((u) => u.online).length;

  const pick = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        aria-label="Person"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="h-auto min-h-[44px] w-full justify-between gap-2 py-1.5 font-normal"
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected ? (
            <>
              <Avatar user={selected} />
              <span className="min-w-0 text-left">
                <span className="block truncate text-sm font-medium">{displayName(selected)}</span>
                {selected.email && <span className="block truncate text-[11px] text-muted-foreground">{selected.email}</span>}
              </span>
            </>
          ) : (
            <>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Users className="h-3.5 w-3.5" />
              </span>
              <span className="truncate text-sm">Everyone ({formatCount(users.length)} people)</span>
            </>
          )}
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-50 transition-transform', open && 'rotate-180')} />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[280px] overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
          <div className="border-b border-border/60 p-2">
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search name, email or machine"
              containerClassName="w-full"
            />
          </div>

          <div className="thin-scrollbar max-h-80 overflow-y-auto overscroll-contain p-1">
            {showEveryone && (
              <Row selected={value === null} onClick={() => pick(null)}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Users className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">Everyone</span>
                  <span className="block text-[11px] text-muted-foreground">{formatCount(users.length)} people</span>
                </span>
              </Row>
            )}
            {matches.map((u) => (
              <Row key={u.userId} selected={value === u.userId} onClick={() => pick(u.userId)}>
                <Avatar user={u} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium" title={displayName(u)}>{displayName(u)}</span>
                  {u.email && <span className="block truncate text-[11px] text-muted-foreground" title={u.email}>{u.email}</span>}
                </span>
                {u.capturesToday > 0 && (
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {formatCount(u.capturesToday)} today
                  </span>
                )}
              </Row>
            ))}
            {matches.length === 0 && !showEveryone && (
              <p className="p-4 text-center text-sm text-muted-foreground">No one matches this search</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Online {formatCount(onlineCount)} · Total {formatCount(users.length)}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors',
        selected ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
      )}
    >
      {children}
      <Check className={cn('h-4 w-4 shrink-0 text-primary', !selected && 'invisible')} />
    </button>
  );
}
